/* cSpell:disable */
import { HeaderConfig } from '@tenants/types/tenant';
import { LOCALE_EXP_REALTY_CA_PATHS } from 'types/locales';
import { ACCESSIBILITY_PATH } from 'constants/urls';

/**
 * Creates a header configuration for the eXp Realty CA tenant.
 *
 * This function generates a standardized header configuration that includes:
 * - Main and authentication button text
 * - Authentication menu items (saved searches, favorites, profile, sign out)
 * - Main navigation menu items
 *
 * @returns A complete header configuration object with all necessary navigation and authentication elements
 */
export function createHeaderConfig(): HeaderConfig {
  const headerConfig: HeaderConfig = {
    mainButtonCopy: {
      key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_MAIN_BUTTON,
      defaultValue: 'Find Your Home',
    },
    authButtonCopy: {
      key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_AUTH_BUTTON,
      defaultValue: 'Log in',
    },
    hasJoinExpPage: true,
    authMenuItems: [
      {
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_AUTH_MENU_ITEMS_MY_SAVED_SEARCHES,
          defaultValue: 'My Saved Searches',
        },
        link: '/account/saved/searches',
        id: 'my-saved-searches',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_AUTH_MENU_ITEMS_MY_FAVOURITES,
          defaultValue: 'My Favourites',
        },
        link: '/account/saved/favourites',
        id: 'my-favourites',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_AUTH_MENU_ITEMS_EDIT_PROFILE,
          defaultValue: 'Edit Profile',
        },
        link: '/account/details',
        id: 'edit-profile',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_AUTH_MENU_ITEMS_SIGN_OUT,
          defaultValue: 'Sign Out',
        },
        id: 'sign-out',
        signOut: true,
      },
    ],
    menuItems: [
      {
        id: 'search',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_SEARCH,
          defaultValue: 'Search',
        },
        link: '/search',
      },
      {
        id: 'buy',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_BUY,
          defaultValue: 'Buy',
        },
        link: '/buy',
      },
      {
        id: 'sell',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_SELL,
          defaultValue: 'Sell',
        },
        link: '/sell',
      },
      {
        id: 'find-an-agent',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_FIND_AN_AGENT,
          defaultValue: 'Find An Agent',
        },
        link: '/agents-search',
      },
      {
        id: 'join-exp',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_JOIN_EXP,
          defaultValue: 'Join eXp',
        },
        link: 'https://join.exprealty.ca',
        newTab: true,
      },
      {
        id: 'exp-agent-login',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_IM_AN_EXP_AGENT,
          defaultValue: "I'm an eXp agent",
        },
        link: 'https://my.exprealty.com/login',
        newTab: true,
      },
      {
        id: 'about',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_ABOUT,
          defaultValue: 'About',
        },
        link: '/explore-exp',
      },
      {
        id: 'sitemap',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_SITEMAP,
          defaultValue: 'Sitemap',
        },
        link: '/sitemap',
      },
      {
        id: 'accessibility',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_ACCESSIBILITY,
          defaultValue: 'Accessibility',
        },
        link: ACCESSIBILITY_PATH,
      },
      {
        id: 'commercial-waitlist',
        label: {
          key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_COMMERCIAL_WAITLIST,
          defaultValue: 'Interested in Commercial?',
        },
        link: '/commercial-waitlist-ca',
        mobileOnly: true,
      },
    ],
    buttonLabels: {
      commercialWaitlist: {
        key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_MENU_ITEMS_COMMERCIAL_WAITLIST,
        defaultValue: 'Interested in Commercial?',
      },
      imReady: {
        key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_IM_READY,
        defaultValue: "I'm Ready",
      },
      contactUs: {
        key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_CONTACT_US,
        defaultValue: 'Contact Us',
      },
      mainButton: {
        key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_MAIN_BUTTON,
        defaultValue: 'Find Your Home',
      },
      authButton: {
        key: LOCALE_EXP_REALTY_CA_PATHS.HEADER_BUTTONS_AUTH_BUTTON,
        defaultValue: 'Log in',
      },
    },
  };

  return headerConfig;
}
